import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export type StatusDialogVariant = 'activate' | 'inactivate' | 'freeze';

export interface StatusConfirmDialogProps {
  open: boolean;
  title?: string;
  description?: string;
  variant: StatusDialogVariant;
  onClose: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
}

const variantStyles: Record<StatusDialogVariant, { color: string; bg: string; icon: React.ReactNode; defaultTitle: string; defaultDescription: string; }> = {
  activate: {
    color: '#2e7d32',
    bg: 'rgba(102, 187, 106, 0.15)',
    icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
    defaultTitle: 'Package Status',
    defaultDescription: 'Would you like to activate your package?'
  },
  inactivate: {
    color: '#ef6c00',
    bg: 'rgba(255, 167, 38, 0.15)',
    icon: <ErrorOutlineIcon sx={{ fontSize: 40 }} />,
    defaultTitle: 'Package Status',
    defaultDescription: 'Would you like to inactivate your package?'
  },
  freeze: {
    color: '#2e7d32',
    bg: 'rgba(102, 187, 106, 0.15)',
    icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
    defaultTitle: 'Package Freeze',
    defaultDescription: 'Would you like to Freeze your package?'
  }
};

export default function StatusConfirmDialog(props: StatusConfirmDialogProps) {
  const { open, title, description, variant, onClose, onConfirm, onCancel } = props;
  const vs = variantStyles[variant];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      PaperProps={{ 
        sx: { 
          borderRadius: '10px',
          maxWidth: 420,
          width: '100%'
        } 
      }}
    >
      <DialogTitle sx={{
        fontFamily: 'Montserrat',
        fontWeight: 600,
        fontStyle: 'normal',
        fontSize: '1.125rem',
        color: '#232323',
        borderBottom: '1px solid rgba(226, 227, 229, 1)',
        pb: 1.5,
        pt: 2,
        pr: 6
      }}>
        {title || vs.defaultTitle}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8,
            color: '#9E9E9E',
            '&:hover': {
              backgroundColor: 'rgba(158, 158, 158, 0.1)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: vs.bg,
              color: vs.color,
            }}
          >
            {vs.icon}
          </Box>
          <Typography
            align="center"
            sx={{
              fontFamily: 'Nunito',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 15,
              textAlign: 'center',
              px: 2
            }}
          >
            {description || vs.defaultDescription}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel || onClose}
            sx={{ 
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1
            }}
          >
            No
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="inherit"
            onClick={onConfirm}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
              color: '#FFF',
              backgroundColor: '#4f4f4f',
              '&:hover': { backgroundColor: '#3d3d3d' },
            }}
          >
            Yes
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
